import React, {useEffect, useRef, useState} from "react";
import styles from './inputs.module.css'
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronDown, faChevronUp, faCloudArrowUp, faX} from "@fortawesome/free-solid-svg-icons";

function TextInput({name, internalName, placeholder, form, setForm, hideName, type, grey, floating}) {
    const [formName, setFormName] = useState(internalName);

    useEffect(() => {
        if (!formName) {
            setFormName(name);
        }
    }, []);

    const setField = (value) => {
        setForm({
            ...form,
            [formName]: value
        });
    }

    return (
        <div className="form-group mb-3" style={{position: 'relative'}}>
            { !hideName && <div className={`${styles.inputLabel} mb-1`}>{name}</div>}
            <input
                style={{
                    backgroundColor: grey ? '#f8fafc' : '#fff'
                }}
                type={type}
                className={`${styles.inputBorderDefault} form-control rounded-2`}
                id={name ? name : internalName}
                value={form[formName] ?? ''}
                onChange={(event) => {
                    setField(event.target.value);
                }}
            />
            <div className={styles.floatingLabel}>
                {placeholder}
            </div>
        </div>
    )
}

function BigTextInput({name, internalName, placeholder, form, setForm}) {
    const [formName, setFormName] = useState(internalName);

    useEffect(() => {
        if (!formName) {
            setFormName(name);
        }
    }, []);

    const setField = (value) => {
        setForm({
            ...form,
            [formName]: value
        });
    }

    return (
        <div className="form-group mb-3">
            <div className={`${styles.inputLabel} mb-1`}>{name}</div>
            <textarea
                style={{
                    backgroundColor: '#f8fafc'
                }}
                className={`${styles.inputBorderDefault} form-control rounded-2`}
                id={name}
                placeholder={placeholder}
                value={form[formName]}
                onChange={(event) => setField(event.target.value)}
            />
        </div>
    )
}

function SelectInput({ name, internalName, options, form, setForm, placeholder,  hideName, grey, greyBackground, floating }) {
    const [formName, setFormName] = useState(internalName);
    const [valueName, setValueName] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        if (!formName) {
            setFormName(name);
        }
    }, []);

    useEffect(() => {
        document.addEventListener('click', handleOutsideClick);

        return () => {
            document.removeEventListener('click', handleOutsideClick);
        };
    }, []);

    useEffect(() => {
        const v = form[formName];
        if (v === undefined || v === null || v === '') {
            return;
        }
        const opt = options?.find((o) => o.id === v);
        if (opt) {
            setValueName(opt.name);
        }
    }, [form[formName], formName, options]);

    const handleOutsideClick = (event) => {
        if (
            dropdownRef.current &&
            !dropdownRef.current.contains(event.target)
        ) {
            setIsOpen(false);
        }
    };


    const toggleDropdown = () => {
        setIsOpen(!isOpen);
    };

    const setField = (value) => {
        setForm((prevForm) => ({
            ...prevForm,
            [formName]: value,
        }));
        setIsOpen(false);
    };

    const setValue = (name, value) => {
        setValueName(name);
        setField(value)
    }

    return (
        <div
            className={`${styles.customSelect} ${isOpen ? styles.customSelectOpen : ''}`}
            ref={dropdownRef}
            style={{userSelect: 'none', position: 'relative'}}
        >
            {!hideName && <div className={`${styles.inputLabel}`}>{name}</div>}
            <div
                className={styles.dropdownButton}
                onClick={toggleDropdown}
                style={{
                    backgroundColor: (grey || greyBackground) ? '#f8fafc' : '#fff'
                }}
            >
                <div>{valueName || <div></div>}</div>
                {isOpen ? <FontAwesomeIcon icon={faChevronUp}/> : <FontAwesomeIcon icon={faChevronDown}/>}
            </div>
            <div className={styles.floatingLabel}>
                {placeholder}
            </div>
            <div className={`${styles.dropdownOptions} ${isOpen ? styles.show : ''}`}>
                {options && options.map((option, index) => (
                    <div
                        key={index}
                        className={`${styles.dropdownOption} ${
                            (grey || greyBackground) ? styles.dropdownOptionTinted : ''
                        } ${
                            form[formName] === option.id ? styles.dropdownOptionSelected : ''
                        }`}
                        onClick={() => setValue(option.name, option.id)}>
                        {option.name}
                    </div>
                ))}
            </div>
        </div>
    );
}

function RadioInput({name, internalName, options, form, setForm}) {
    const [formName, setFormName] = useState(internalName);
    const [checked, setChecked] = useState(form[formName]);

    useEffect(() => {
        if (!formName) {
            setFormName(name);
        }
    }, []);

    const setField = (value) => {
        setForm({
            ...form,
            [formName]: value
        });
    }

    const handleRadioChange = (index) => {
        setChecked(index);
        setField(index);
    }

    useEffect(() => {
        setChecked(form[formName]);
    }, [form[formName]]);

    return (
        <div className="form-group mb-3">
            <div className={`${styles.inputLabel} mb-1`}>{name}</div>
            <div className={styles.customRadio}>
                {options.map((option, index) => (
                    <div key={index} className={styles.radioOption}>
                        <input
                            type="radio"
                            id={`${name}-${index}`}
                            name={name}
                            checked={checked === index}
                            onChange={() => handleRadioChange(index)}
                        />
                        <label htmlFor={`${name}-${index}`}>{option}</label>
                    </div>
                ))}
            </div>
        </div>
    );
}

function FileInput({name, internalName, form, setForm, onChange, accept, comfortable}) {
    const [formName, setFormName] = useState(internalName);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (!formName) {
            setFormName(name);
        }
    }, []);

    const keyForFile = internalName || formName || name;

    const handleFileClick = () => {
        fileInputRef.current.click();
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setDragActive(true);
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const related = event.relatedTarget;
        const stillInside = !!(related && event.currentTarget.contains(related));
        if (stillInside) {
            return;
        }
        setDragActive(false);
    };

    const applyFileList = (fileList) => {
        if (!fileList || fileList.length === 0) {
            return;
        }
        const files = Array.from(fileList);
        setSelectedFiles(files);
        if (onChange) {
            const dt = new DataTransfer();
            for (const f of files) {
                dt.items.add(f);
            }
            onChange({ target: { files: dt.files } });
        } else {
            const file = files[0];
            setForm((prev) => ({
                ...prev,
                [keyForFile]: file,
            }));
        }
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setDragActive(false);
        applyFileList(event.dataTransfer?.files);
    };

    const handleFileChange = (event) => {
        const files = Array.from(event.target.files || []);
        setSelectedFiles(files);
        if (onChange) {
            onChange(event);
        } else if (files.length > 0) {
            setForm((prev) => ({
                ...prev,
                [keyForFile]: files[0],
            }));
        }
    };

    return (
        <div className={`${styles.fileInputOutsideContainer} ${comfortable ? styles.fileInputComfortable : ''}`}>
            <div
                className={
                    comfortable
                        ? `${styles.fileInputContainer} py-0 ${styles.fileInputComfortableDrop} ${dragActive ? styles.fileInputComfortableDropActive : ''}`
                        : `${styles.fileInputContainer} py-1`
                }
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={
                    comfortable
                        ? undefined
                        : {
                              border: '2px dashed',
                              borderColor: dragActive ? '#4A4DBB' : '#bdbdbd',
                              borderRadius: '8px',
                              minHeight: '52px',
                              marginBottom: '4px',
                              backgroundColor: dragActive ? 'rgba(74, 77, 187, 0.06)' : 'transparent',
                              transition: 'border-color 0.15s ease, background-color 0.15s ease',
                          }
                }
            >
                <div
                    className={styles.fileInput}
                    onClick={handleFileClick}
                >
                    <div className={styles.uploadText}>
                        <div
                            className={
                                comfortable
                                    ? styles.fileInputComfortableLabel
                                    : 'd-flex mx-2 my-1 px-2 py-1 align-items-center'
                            }
                            style={
                                comfortable
                                    ? undefined
                                    : {
                                          borderRadius: '6px',
                                          backgroundColor: '#D9D9D9',
                                      }
                            }
                        >
                            {selectedFiles.length > 0 ? (
                                <div>
                                    {selectedFiles.map((file, index) => (
                                        <div key={index}>{file.name}</div>
                                    ))}
                                </div>
                            )
                                :
                                <div>{name}</div>
                            }
                        </div>
                        <div
                            className={comfortable ? styles.fileInputComfortableHint : 'mt-1 px-2'}
                            style={
                                comfortable
                                    ? undefined
                                    : {
                                          fontSize: '12px',
                                          color: '#555',
                                          textAlign: 'center',
                                      }
                            }
                        >
                            Arrastra el archivo aquí o haz clic para elegir
                        </div>
                    </div>
                </div>
                <input
                    type="file"
                    accept={accept}
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                />
            </div>
        </div>
    )
}

function DateInput({name, internalName, form, setForm, hideName, placeholder, floating}) {
    const [formName, setFormName] = useState(internalName);

    useEffect(() => {
        if (!formName) {
            setFormName(name);
        }
    }, []);

    const setField = (value) => {
        setForm({
            ...form,
            [formName]: value
        });
    }

    return (
        <div className="form-group mb-3" style={{position: "relative"}}>
            <input
                type={'text'}
                className={`${styles.inputBorderDefault} form-control rounded-2`}
                id={name}
                value={form[formName]}
                onFocus={(event) => {
                    event.target.type = 'date';
                }}
                onChange={(event) => setField(event.target.value)}
            />
            <div className={styles.floatingLabel}>
                {placeholder}
            </div>
            {!hideName && <div className={`${styles.inputLabel} mb-1`} style={{
                fontSize: '12px',
                color: '#9b9b9b',
                textAlign: 'center'
            }}>{name}</div>}
        </div>
    )
}

function PrimaryButton({name, onClick, style, variant = 'solid'}) {
    const cls =
        variant === 'quiet'
            ? `${styles.primaryButton} ${styles.primaryButtonQuiet}`
            : styles.primaryButton;
    return (
        <button type="button" className={cls} style={style} onClick={onClick}>
            {name}
        </button>
    );
}

function SecondaryButton({name, onClick}) {
    return (
        <button type="button" className={styles.secondaryButton} onClick={onClick}>
            {name}
        </button>
    );
}

function DangerButton({name, onClick, variant = 'neutral'}) {
    const cls =
        variant === 'destructive'
            ? `${styles.dangerButton} ${styles.dangerButtonDestructive}`
            : styles.dangerButton;
    return (
        <button type="button" className={cls} onClick={onClick}>
            {variant !== 'destructive' ? (
                <FontAwesomeIcon icon={faX} className={styles.dangerBtnIcon} aria-hidden />
            ) : null}
            <span>{name}</span>
        </button>
    );
}

export {
    TextInput,
    BigTextInput,
    SelectInput,
    RadioInput,
    FileInput,
    DateInput,

    PrimaryButton,
    SecondaryButton,
    DangerButton
};
